/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useCallback, useRef, useState } from 'react';

export type ZipCodeStatus = 'idle' | 'loading' | 'success' | 'not_found' | 'error';

export interface ZipCodeAddress {
  street: string;       // logradouro
  neighborhood: string; // bairro
  city: string;         // localidade
  state: string;        // uf
  /** 7-digit IBGE municipality code (ViaCEP `ibge`). Payien needs it for the NFS-e
   *  tomador; it used to be discarded, which forced Payien to guess a municipality. */
  codigo_municipio: string;
}

export interface UseZipCodeResult {
  status: ZipCodeStatus;
  address: ZipCodeAddress | null;
  /** Call with 8-digit (or masked) CEP string; strips non-digits internally. */
  lookup: (cep: string) => Promise<void>;
  reset: () => void;
}

/**
 * Builds the ViaCEP lookup URL for a set of postal-code digits.
 *
 * @param digits - The 8-digit postal code.
 * @returns The ViaCEP JSON endpoint URL.
 */
const VIACEP_URL = (digits: string) =>
  `https://viacep.com.br/ws/${digits}/json/`;

const resolvedCepCache = new Map<string, ZipCodeAddress>();

function cachedAddressFor(cep?: string): ZipCodeAddress | null {
  const digits = (cep || '').replace(/\D/g, '');
  return digits.length === 8 ? (resolvedCepCache.get(digits) ?? null) : null;
}

/**
 * Fetches a Brazilian address from ViaCEP by postal code.
 *
 * Business rules:
 * - Lookup only fires when the sanitised input reaches exactly 8 digits.
 * - On success, the returned `address` can be used to autofill form fields.
 * - After autofill the caller may focus an arbitrary `nextFieldRef` so mobile
 *   users are taken straight to the Number field (UX anticipation pattern).
 * - On `{ erro: true }` ViaCEP response, status becomes `not_found`.
 *
 * Field mapping: logradouro → street, bairro → neighborhood,
 *                localidade → city, uf → state, ibge → codigo_municipio.
 */
export function useZipCode(initialCep?: string): UseZipCodeResult {
  const initialCached = cachedAddressFor(initialCep);
  const [status, setStatus] = useState<ZipCodeStatus>(initialCached ? 'success' : 'idle');
  const [address, setAddress] = useState<ZipCodeAddress | null>(initialCached);

  const lookup = useCallback(async (cep: string) => {
    const digits = cep.replace(/\D/g, '');
    if (digits.length !== 8) return;

    setStatus('loading');
    setAddress(null);

    try {
      const response = await fetch(VIACEP_URL(digits));
      if (!response.ok) throw new Error('Network error');

      const data = await response.json();

      if (data.erro) {
        setStatus('not_found');
        return;
      }

      const resolved: ZipCodeAddress = {
        street: data.logradouro ?? '',
        neighborhood: data.bairro ?? '',
        city: data.localidade ?? '',
        state: data.uf ?? '',
        codigo_municipio: data.ibge ?? '',
      };
      resolvedCepCache.set(digits, resolved);
      setAddress(resolved);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }, []);

  const reset = useCallback(() => {
    setStatus('idle');
    setAddress(null);
  }, []);

  return { status, address, lookup, reset };
}

/**
 * Re-export legacy name so existing imports of `useCepLookup` can migrate
 * incrementally without breaking the build.
 *
 * @deprecated Use `useZipCode` instead.
 */
export { useZipCode as useCepLookup };

// Re-export types under both names for consumers that imported the old types.
export type { ZipCodeAddress as CepAddress };
export type { UseZipCodeResult as UseCepLookupResult };
export type { ZipCodeStatus as CepStatus };

/**
 * Convenience hook that builds a `React.RefObject` managed focus helper.
 * Attach `nextFieldRef` to the DOM input that should receive focus after
 * the CEP autofill completes (typically the "Number" field).
 *
 * Usage:
 *   const { nextFieldRef, focusNextField } = useFocusAfterAutofill();
 *   // In the useEffect that applies autofill data:
 *   focusNextField();
 */
export function useFocusAfterAutofill() {
  const nextFieldRef = useRef<HTMLInputElement | null>(null);

  const focusNextField = useCallback(() => {
    // rAF ensures the DOM has finished the re-render cycle before focusing.
    requestAnimationFrame(() => {
      nextFieldRef.current?.focus();
    });
  }, []);

  return { nextFieldRef, focusNextField };
}
